import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, RefreshCw, Search, X } from 'lucide-react';
import { erpAPI } from '../../services/api';
import SearchableSelect from '../../components/admin/SearchableSelect';
import Toast from '../../components/admin/Toast';
import FinancialDocumentDrawer from '../../components/admin/FinancialDocumentDrawer';
import { exportCSV } from '../../utils/exportUtils';

const money = (v: any) => `\u09F3${Number(v || 0).toLocaleString('en-BD', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (v: any) => v ? new Date(v).toLocaleDateString('en-BD') : '-';
const rows = (p: any): any[] => Array.isArray(p) ? p : (p?.results || []);
const errorText = (e: any) => e?.response?.data?.detail || e?.message || 'Request failed';

const SOURCE_ROUTES: Record<string, string> = {
  Sale: '/admin/sale-orders', Purchase: '/admin/purchases/history',
  EcommerceOrder: '/admin/orders', StockTransfer: '/admin/inventory/transfers',
  TransferDestination: '/admin/inventory/transfers', TransferSource: '/admin/inventory/transfers',
  CODSettlement: '/admin/accounts/cash-bank', CashTransfer: '/admin/accounts/cash-bank',
  Expense: '/admin/accounts/expenses', ExpensePayment: '/admin/accounts/expenses',
  SupplierBillPayment: '/admin/accounts/payables', POSExchange: '/admin/pos-exchanges',
  ReceivableReceipt: '/admin/accounts/receivables',
};

export default function JournalEntriesPage({ me, locationFilter }: { me: any; locationFilter: string }) {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [selectedEntry, setSelectedEntry] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const showLoc = useMemo(() => !locationFilter, [locationFilter]);
  const params = useMemo(() => {
    const p: Record<string, any> = { ...filters };
    if (locationFilter) p.location = locationFilter;
    return p;
  }, [filters, locationFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await erpAPI.list('journal-entries', { ...params, page_size: 100 });
      setData(resp.data?.results || rows(resp.data));
    } catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const openDrawer = (entry: any) => {
    setSelectedEntry(entry);
    setDrawerOpen(true);
  };

  const sourceUrl = (refType: string, refId: string) => {
    const base = SOURCE_ROUTES[refType];
    return base ? `${base}?id=${refId}` : null;
  };

  const filtered = useMemo(() => {
    let list = [...data];
    if (filters.search) {
      const q = filters.search.toLowerCase();
      list = list.filter(r => (r.entry_number || '').toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q) || (r.reference_type || '').toLowerCase().includes(q) || String(r.reference_id || '').includes(q));
    }
    if (filters.reference_type) list = list.filter(r => r.reference_type === filters.reference_type);
    return list;
  }, [data, filters]);

  const refTypeOptions = useMemo(() => {
    const types = [...new Set(data.map((r: any) => r.reference_type).filter(Boolean))] as string[];
    return types.map(t => ({ value: t, label: t }));
  }, [data]);

  const drawerLines = selectedEntry?.lines || [];
  const totalDebit = drawerLines.reduce((s: number, l: any) => s + Number(l.debit || 0), 0);
  const totalCredit = drawerLines.reduce((s: number, l: any) => s + Number(l.credit || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  return <div className="erp-page">
    <Toast show={Boolean(message)} message={message?.text || ''} type={message?.type || 'info'} onClose={() => setMessage(null)} />

    <div className="erp-filterbar" style={{ flexWrap: 'wrap' }}>
      <Search size={15} style={{ color: '#94a3b8' }} />
      <input value={filters.search || ''} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
        placeholder="Search entry #, description, reference..." style={{ border: 0, background: 'transparent', flex: 1, minWidth: 180, outline: 'none' }} />
      <label>Module</label>
      <SearchableSelect value={filters.reference_type || ''} onChange={n => setFilters(f => ({ ...f, reference_type: n }))}
        placeholder="All" options={refTypeOptions} />
      {Object.keys(filters).some(k => filters[k]) && (
        <button onClick={() => setFilters({})} style={{ border: 0, background: '#fee2e2', color: '#991b1b', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontWeight: 600, fontSize: '.7rem' }}>
          <X size={12} /> Clear
        </button>
      )}
      <button onClick={() => exportCSV(filtered.map((r: any) => ({ entry_number: r.entry_number, date: r.date, module: r.reference_type, reference: r.reference_id, description: r.description, debit: r.total_debit || 0, credit: r.total_credit || 0, status: r.posting_status })), 'journal-entries')}
        className="erp-btn secondary" style={{ padding: '5px 8px', fontSize: '.7rem', marginLeft: 'auto' }}>
        <Download size={12} /> CSV
      </button>
    </div>

    <div className="acct-kpi-grid" style={{ marginBottom: 0 }}>
      <div className="erp-stat"><span>Total Entries</span><strong>{filtered.length}</strong></div>
      <div className="erp-stat"><span>Total Debit</span><strong style={{ color: '#dc2626' }}>
        {money(filtered.reduce((s: number, r: any) => s + Number(r.total_debit || r.lines?.reduce?.((a: number, l: any) => a + Number(l.debit || 0), 0) || 0), 0))}</strong></div>
      <div className="erp-stat"><span>Total Credit</span><strong style={{ color: '#059669' }}>
        {money(filtered.reduce((s: number, r: any) => s + Number(r.total_credit || r.lines?.reduce?.((a: number, l: any) => a + Number(l.credit || 0), 0) || 0), 0))}</strong></div>
    </div>

    {loading ? <div className="erp-loading"><RefreshCw className="spin" /> Loading...</div> :
      <section className="erp-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="erp-table-wrap" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
          <table style={{ position: 'relative' }}>
            <thead><tr style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff' }}>
              <th>Journal #</th>{showLoc && <th>Location</th>}<th>Date</th><th>Module</th><th>Reference</th><th>Description</th><th>Debit</th><th>Credit</th><th>Status</th><th>By</th>
            </tr></thead>
            <tbody>{filtered.length === 0 ? <tr><td className="erp-empty" colSpan={showLoc ? 11 : 10}>No journal entries found.</td></tr> :
              filtered.map((r: any) => <tr key={r.id || r.entry_number}
                onClick={() => openDrawer(r)} style={{ cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ fontWeight: 600, fontSize: '.78rem', fontFamily: 'monospace' }}>{r.entry_number || '-'}</td>
                {showLoc && <td><span style={{ color: '#4f46e5', fontWeight: 600, fontSize: '.75rem' }}>{r.location_name || r.location || '-'}</span></td>}
                <td style={{ fontSize: '.78rem' }}>{date(r.date)}</td>
                <td><span className="erp-status">{r.reference_type || '-'}</span></td>
                <td style={{ fontSize: '.78rem' }}>{r.reference_id || '-'}</td>
                <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '.8rem' }}>{r.description || '-'}</td>
                <td style={{ color: '#dc2626', fontWeight: 600 }}>{money(r.total_debit || r.lines?.reduce?.((s: number, l: any) => s + Number(l.debit || 0), 0) || 0)}</td>
                <td style={{ color: '#059669', fontWeight: 600 }}>{money(r.total_credit || r.lines?.reduce?.((s: number, l: any) => s + Number(l.credit || 0), 0) || 0)}</td>
                <td><span className={`erp-status ${r.posting_status === 'POSTED' ? 'received' : ''}`}
                  style={r.posting_status === 'DRAFT' ? { background: '#fef3c7', color: '#92400e' } : r.posting_status === 'APPROVED' ? { background: '#dbeafe', color: '#1e40af' } : {}}>
                  {r.posting_status || 'POSTED'}</span></td>
                <td style={{ fontSize: '.75rem', color: '#64748b' }}>{r.created_by_name || r.created_by || '-'}</td>
              </tr>)}</tbody>
          </table>
        </div>
      </section>}

    <FinancialDocumentDrawer
      open={drawerOpen} onClose={() => setDrawerOpen(false)}
      documentType="journal" title={`Journal Entry — ${selectedEntry?.entry_number || ''}`}
      subtitle={selectedEntry?.description}
      fields={[
        { label: 'Date', value: selectedEntry?.date ? date(selectedEntry.date) : '-' },
        { label: 'Module', value: <span className="erp-status">{selectedEntry?.reference_type || '-'}</span> },
        { label: 'Reference', value: selectedEntry?.reference_id || '-' },
        { label: 'Business Location', value: <span style={{ color: '#4f46e5', fontWeight: 600 }}>{selectedEntry?.location_name || selectedEntry?.location || '-'}</span> },
        { label: 'Status', value: <span className={`erp-status ${selectedEntry?.posting_status === 'POSTED' ? 'received' : ''}`} style={selectedEntry?.posting_status === 'DRAFT' ? { background: '#fef3c7', color: '#92400e' } : selectedEntry?.posting_status === 'APPROVED' ? { background: '#dbeafe', color: '#1e40af' } : {}}>
          {selectedEntry?.posting_status || 'POSTED'}</span> },
        { label: 'Created By', value: selectedEntry?.created_by_name || selectedEntry?.created_by || '-' },
      ]}
      timeline={[
        { label: 'Created', date: selectedEntry?.date ? date(selectedEntry.date) : undefined, active: true },
        { label: selectedEntry?.posting_status === 'DRAFT' ? 'Draft' : 'Approved', active: selectedEntry?.posting_status !== 'DRAFT' },
        { label: 'Posted', active: selectedEntry?.posting_status === 'POSTED' },
      ]}
      sourceLink={(() => {
        const refType = selectedEntry?.reference_type; const refId = selectedEntry?.reference_id;
        if (refType && refId) { const url = sourceUrl(refType, String(refId)); if (url) return { label: `${refType} #${refId}`, url }; }
        return undefined;
      })()}
    >
      <div style={{ marginTop: 8 }}>
        <h4 style={{ margin: '0 0 10px', fontSize: '.85rem' }}>Journal Lines</h4>
        <div style={{ maxHeight: 250, overflowY: 'auto', borderRadius: 8, border: '1px solid var(--admin-border)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.8rem' }}>
            <thead><tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
              <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--admin-border)', fontSize: '.68rem', textTransform: 'uppercase', color: '#64748b' }}>Account</th>
              <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid var(--admin-border)', fontSize: '.68rem', textTransform: 'uppercase', color: '#64748b' }}>Debit</th>
              <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid var(--admin-border)', fontSize: '.68rem', textTransform: 'uppercase', color: '#64748b' }}>Credit</th>
            </tr></thead>
            <tbody>{drawerLines.length === 0 ? <tr><td colSpan={3} style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No lines</td></tr> :
              drawerLines.map((l: any, i: number) => <tr key={i}>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ fontWeight: 500 }}>{l.account_name || l.account_code || l.account || '-'}</span>
                  {l.account_code && <span style={{ fontSize: '.7rem', color: '#94a3b8', marginLeft: 6 }}>({l.account_code})</span>}
                </td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>
                  {Number(l.debit) > 0 ? money(l.debit) : '-'}</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', color: '#059669', fontWeight: 600 }}>
                  {Number(l.credit) > 0 ? money(l.credit) : '-'}</td>
              </tr>)}</tbody>
            <tfoot><tr style={{ background: '#f8fafc', fontWeight: 700 }}>
              <td style={{ padding: '10px', borderTop: '2px solid var(--admin-border)' }}>Totals</td>
              <td style={{ padding: '10px', textAlign: 'right', borderTop: '2px solid var(--admin-border)', color: isBalanced ? '#059669' : '#dc2626' }}>{money(totalDebit)}</td>
              <td style={{ padding: '10px', textAlign: 'right', borderTop: '2px solid var(--admin-border)', color: isBalanced ? '#059669' : '#dc2626' }}>{money(totalCredit)}</td>
            </tr></tfoot>
          </table>
        </div>
        <div style={{ marginTop: 8, textAlign: 'center' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 999, fontSize: '.75rem', fontWeight: 700, background: isBalanced ? '#d1fae5' : '#fee2e2', color: isBalanced ? '#065f46' : '#991b1b' }}>
            {isBalanced ? '✓ Balanced' : '✗ Unbalanced'}
          </span>
        </div>
      </div>
    </FinancialDocumentDrawer>
  </div>;
}
